import { FieldType } from "../../DynamicGroup/DynamicGroup";
import { CustomFieldTypes } from "../Enums/CustomFieldTypes";
import { BaseFormFieldWithHistory } from "../Typings/Campaigns";
import { IClientCustomField } from "../Typings/Client";
import { Filter, Rule } from "../Typings/DynamicGroupRules";

const MaxRuleCount = 5;

function convertToFieldType(type: CustomFieldTypes): string {
  if (type === CustomFieldTypes.ArrayString) return "dropdown";
  if (type === CustomFieldTypes.Upload) return "upload";
  return "text";
}

function extractDropdownOptions(fields: BaseFormFieldWithHistory[], key: string): string[] {
  return fields
    .filter((field) => field.key === key)
    .flatMap((field) =>
      field.adGroupOptions && field.adGroupOptions.length > 0
        ? field.adGroupOptions.map((option) => option.name).sort()
        : field.dropdownOptions,
    );
}

function convertCustomFieldsToFieldTypes(
  customFields: IClientCustomField[],
  forms?: { formFields: Record<string, BaseFormFieldWithHistory> }[],
): FieldType[] {
  const formFieldsList =
    (forms?.flatMap((form) => Object.values(form.formFields)) as BaseFormFieldWithHistory[]) ?? [];

  const uniqueFields: FieldType[] = [];

  customFields.forEach((customField) => {
    const fieldId = `customfield.${customField.key}`;
    const alreadyExists = uniqueFields.some((field) => field.id === fieldId);

    if (!alreadyExists) {
      uniqueFields.push({
        id: fieldId,
        field: {
          fieldName: customField.label,
          fieldType: convertToFieldType(customField.fieldType),
          archived: customField.isArchived,
          dropdownOptions: extractDropdownOptions(formFieldsList, customField.key),
        },
      } as FieldType);
    }
  });

  return uniqueFields;
}

function hasArchivedFieldInRule(rule: Rule, archivedFieldIds: string[]): boolean {
  if (archivedFieldIds.includes(rule.filter.field)) {
    return true;
  }

  if (rule.rules && rule.rules.length > 0) {
    return rule.rules.some((subRule) => hasArchivedFieldInRule(subRule, archivedFieldIds));
  }

  return false;
}

function allRulesAreNonArchived(rules: Rule[], archivedFieldIds: string[]): boolean {
  function isRuleNonArchived(rule: Rule): boolean {
    if (archivedFieldIds.includes(rule.filter.field)) {
      return false;
    }

    if (rule.rules && rule.rules.length > 0) {
      return rule.rules.every(isRuleNonArchived);
    }

    return true;
  }

  return rules.every(isRuleNonArchived);
}

const isValidRule = (rule: Rule): boolean => {
  if (rule.filter.field === "" || rule.filter.command === "" || rule.filter.type === "") {
    return false;
  }

  if (rule.filter.type === "location") {
    return isValidLocation(rule.filter);
  }

  if (rule.filter.value.value === "") {
    return false;
  }

  if (rule.rules && rule.rules.length > 0) {
    return rule.rules.every((r) => isValidRule(r));
  }

  return true;
};

const isValidLocation = (filter: Filter): boolean => {
  const lat = parseFloat(filter.value.latitude);
  const lon = parseFloat(filter.value.longitude);
  const radius = parseFloat(filter.value.radius);
  const validCoordinates = !isNaN(lat) && !isNaN(lon) && !(lat === 0 && lon === 0);
  const validRadius = !isNaN(radius) && radius > 0;
  const validName = !!filter.value.name;
  return validName && validCoordinates && validRadius;
};

const hasInvalidRule = (rules: Rule[]): boolean => {
  return rules.some((r) => !isValidRule(r));
};

const checkForChanges = (rules: Rule[], pendingRules: Rule[]) => {
  return JSON.stringify(rules) !== JSON.stringify(pendingRules);
};

const getRuleCount = (rule: Rule) => {
  let count = 1;
  if (rule.filter.field === "" && rule.filter.value === "") {
    count = 0;
  }
  if (rule.rules && rule.rules.length > 0) {
    rule.rules.forEach((r) => {
      count += getRuleCount(r);
    });
  }
  return count;
};

export {
  MaxRuleCount,
  convertCustomFieldsToFieldTypes,
  hasArchivedFieldInRule,
  allRulesAreNonArchived,
  isValidRule,
  isValidLocation,
  hasInvalidRule,
  checkForChanges,
  getRuleCount,
};
